import { memo, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AdvertBanners } from "../../../model/advert-banner/advert-banners";
import { useLazyAdvertBannersQuery, useStopAdvertBannerMutation } from "../../../service/advert-banner-service";
import styles from "./AdvertBannerPage.module.scss";
import CommonPagination from "../../../common/components/pagination/CommonPagination";
import CreateBannerSideDialog from "./create-banner-side-dialog/CreateBannerSideDialog";
import CommonLoadingSpinner from "../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import Button from "../../../common/components/xepp/button/Button";

type Props = {};

const AdvertBannerPage = memo((props: Props) => {
	const navigate = useNavigate();

	const pageSize = 3;
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [loading, setLoading] = useState(false);

	const [data, setData] = useState<AdvertBanners>();
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

    const [getAdvertBanners, { isLoading: isLoadingBanners }] = useLazyAdvertBannersQuery();
	const [stopAdvertBanner, { isLoading: isStoppingBanner }] = useStopAdvertBannerMutation();

	const fetchAdvertBanners = useCallback(async () => {
		setLoading(true);
		try {
            const params = {
                page: currentPage,
                pageSize: pageSize
            }
		    let response = await getAdvertBanners(params).unwrap();
			setTotalPages(response.pastBanners.totalPages);
            setData(response);
		} catch (error) {
			toast.error("Error loading Advert Banners");
		}
		setLoading(false);
	}, [getAdvertBanners, currentPage]);

	const refetchData = useCallback(() => {
		fetchAdvertBanners();
	}, [fetchAdvertBanners]);

	useEffect(() => {
		refetchData();
	}, [refetchData]);

	const handleStopClick = async (id: number) => {
		try {
			await stopAdvertBanner({ id: id }).unwrap();
			toast.success("Banner stopped successfully");
			fetchAdvertBanners();
		} catch (error) {
			toast.error("Failed to stop the banner");
		}
	}

	const openDialog = (): void => {
        setIsDialogOpen(true);
    }

    const closeDialog = (): void => {
        setIsDialogOpen(false);
    }

	return (
		<CommonLoadingSpinner loading={loading}>
			<div className={styles.container}>
				<div className={styles.tableContainer}>
					<div className={styles.tableHeader}>
						<div className={styles.tableTitle}>Store Banner</div>
						<Button 
						  text="Create Banner"
						  onClick={() => openDialog()}
						/>
					</div>
					{data?.activeBanner ? (
						<div className={styles.tableContent}>
							<table className="standardTable">
								<thead>
									<tr>
										<th>BANNER NAME</th>
										<th>IMAGE</th>
										<th>VIEWS</th>
										<th>CLICKS</th>
										<th>DATE ADDED</th>
										<th>ACTION</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>{data?.activeBanner.name}</td>
										<td>
											<img src={data?.activeBanner.pictureUrl} alt="Banner" />
										</td>
										<td>{data?.activeBanner.numberOfViews}</td>
										<td>{data?.activeBanner.numberOfClicks}</td>
										<td>{new Date(data?.activeBanner.uploadedAt).toLocaleString('en-US', {
											hour: 'numeric',
											minute: 'numeric',
											hour12: true,
											day: 'numeric',
											month: 'short',
											year: 'numeric',
										})}
										</td>
										<td>
											<button className={`standardButton ${styles.stopBtn}`} onClick={() => handleStopClick(data?.activeBanner.id)}>STOP</button>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					) : (
						<div className={styles.noActiveContainer}>
							<p>
								There is no active banner at the moment
							</p>
						</div>
					)}
				</div>

				<div className={styles.tableContainer}>
					<div className={styles.tableHeader}>
						<div className={styles.tableTitle}>History</div>
					</div>
					<div className={styles.tableContent}>
						<table className="standardTable">
							<thead>
								<tr>
									<th>BANNER NAME</th>
									<th>IMAGE</th>
									<th>VIEWS</th>
									<th>CLICKS</th>
									<th>DATE ADDED</th>
									<th>DATE REMOVED</th>
								</tr>
							</thead>
							<tbody>
								{data?.pastBanners.items.map((banner) => (
									<tr key={banner.id}>
										<td>{banner.name}</td>
										<td>
											<img src={banner.pictureUrl} alt="Banner" />
										</td>
										<td>{banner.numberOfViews}</td>
										<td>{banner.numberOfClicks}</td>
										<td>{new Date(banner.uploadedAt).toLocaleString('en-US', {
											hour: 'numeric',
											minute: 'numeric',
											hour12: true,
											day: 'numeric',
											month: 'short',
											year: 'numeric',
										})}
										</td>
										<td>
											{banner.stoppedAt 
											? new Date(banner.stoppedAt).toLocaleString('en-US', {
												hour: 'numeric',
												minute: 'numeric',
												hour12: true,
												day: 'numeric',
												month: 'short',
												year: 'numeric',
											})
											: '-'}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<div className={styles.paginationContainer}>
						<CommonPagination
							currentPage={currentPage}
							isLoading={loading}
							numberOfPages={totalPages}
							onPageChange={(page: number) => {
								setCurrentPage(page);
							}}
						/>
					</div>
				</div>

				{isDialogOpen && (
					<CreateBannerSideDialog onCloseDialog={closeDialog} onSuccess={fetchAdvertBanners} setLoading={setLoading} />
				)}
			</div>
		</CommonLoadingSpinner>
	);
});

export default AdvertBannerPage;
